import React, { useState } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { CloudUpload, Delete } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const requiredDocs = [
  { name: "passport", label: "Passport", required: false },
  { name: "transcripts", label: "Transcripts for the past three years", required: false },
  { name: "graduationCertificate", label: "Graduation Certificate", required: false },
  { name: "idPhoto", label: "ID Photo", required: false },
  { name: "other", label: "Other (Optional)", required: false },
];

const FileUpload = ({ onFilesChange, initialFiles = {} }) => {
  const [files, setFiles] = useState(initialFiles);
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();
  const language = useSelector((state) => state.language.language)

  const updateFiles = (newFiles) => {
    setFiles(newFiles);
    if (onFilesChange) {
      onFilesChange(newFiles);
    }
  };

  const onDrop = (acceptedFiles, fieldName) => {
    if (acceptedFiles.length > 0) {
      const newFiles = { ...files, [fieldName]: acceptedFiles[0] };
      updateFiles(newFiles);
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  const handleRemove = (fieldName) => {
    const newFiles = { ...files };
    delete newFiles[fieldName];
    updateFiles(newFiles);
  };

  const validateFiles = () => {
    const newErrors = {};
    requiredDocs.forEach(({ name, required }) => {
      if (required && !files[name]) {
        newErrors[name] = "This document is required";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateFiles()) {
      console.log("Uploading files:", files);
      alert("Files uploaded successfully!");
    }
  };

  return (
    <Box sx={{ border: "0px solid black", p: 3, mt: 2, borderRadius: 2 }}>
      {requiredDocs.map(({ name, label, required }) => (
        <FileUploadField
          key={name}
          label={t(label)}
          required={required}
          file={files[name]}
          onDrop={(files) => onDrop(files, name)}
          onRemove={() => handleRemove(name)}
          error={errors[name]}
        />
      ))}

      {/* <Button
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 3 }}
        onClick={handleSubmit}
      >
        Upload Documents
      </Button> */}
    </Box>
  );
};

const FileUploadField = ({ label, required, file, onDrop, onRemove, error }) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => onDrop(acceptedFiles),
    multiple: false,
    accept: "image/*,.pdf",
  });

  const isPdf = file && file.type === "application/pdf";
  const fileUrl = file ? URL.createObjectURL(file) : null;

  return (
    <Box sx={{ mb: 10 }}>
      <Typography sx={{ fontWeight: "bold" }}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </Typography>
      {!file && (
        <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed gray",
          p: 2,
          textAlign: "center",
          cursor: "pointer",
          borderRadius: 1,
          bgcolor: "#f9f9f9",
          "&:hover": { bgcolor: "#eee" },
        }}
      >
        <input {...getInputProps()} />
        {file ? (
          <Typography variant="body2">{file.name}</Typography>
        ) : (
          <>
            <CloudUpload sx={{ fontSize: 40, color: "gray" }} />
            <Typography variant="body2">Drag & Drop or Click to Upload</Typography>
          </>
        )}
      </Box>
      )}
     
      
      {file && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="body2" color="primary" sx={{ flexGrow: 1 }}>
              {file.name}
            </Typography>
            <IconButton onClick={onRemove} color="error" size="small">
              <Delete />
            </IconButton>
          </Box>
          
          {/* PDF Preview */}
          {isPdf && fileUrl && (
            <Box sx={{ mt: 1, border: "1px solid #ddd", borderRadius: 1 }}>
              <iframe
                src={fileUrl}
                title={file.name}
                width="100%"
                height="400px"
                style={{ border: "none", borderRadius: "4px" }}
              />
            </Box>
          )}
          
          {/* Image Preview */}
          {!isPdf && fileUrl && file.type.startsWith("image/") && (
            <Box sx={{ mt: 1, textAlign: "center" }}>
              <img
                src={fileUrl}
                alt={file.name}
                style={{ 
                  maxWidth: "100%", 
                  maxHeight: "300px", 
                  border: "1px solid #ddd",
                  borderRadius: "4px"
                }}
              />
            </Box>
          )}
        </Box>
      )}
      {error && <Typography color="error" variant="body2">{error}</Typography>}
    </Box>
  );
};

export default FileUpload;